/**
 * Composable для Web Push подписки
 */
import { ref, computed } from 'vue'
import { notificationsApi } from '../api/notifications.api'
import type { WebPushPermissionState } from '../types/notifications.types'

export const useWebPush = () => {
  const isSupported = computed(() => 'serviceWorker' in navigator && 'PushManager' in window)
  const permissionState = ref<WebPushPermissionState>(
    isSupported.value ? (Notification.permission as WebPushPermissionState) : 'denied',
  )
  const isSubscribed = ref(false)
  const isLoading = ref(false)

  async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!isSupported.value) return null
    try {
      return await navigator.serviceWorker.register('/sw.js')
    } catch (error) {
      console.error('Service Worker registration failed:', error)
      return null
    }
  }

  async function subscribe(): Promise<boolean> {
    if (!isSupported.value) return false
    isLoading.value = true

    try {
      // DEV-2685. Safari на iPhone спрашивает разрешение только из обработчика клика, а
      // после await (регистрация воркера, ключ VAPID с сервера) жест уже истёк —
      // pushManager.subscribe упал бы с NotAllowedError. Поэтому спрашиваем первым делом.
      if (Notification.permission === 'default') await Notification.requestPermission()
      permissionState.value = Notification.permission as WebPushPermissionState
      if (permissionState.value !== 'granted') return false

      const registration = await registerServiceWorker()
      if (!registration) return false

      // Получаем VAPID ключ с сервера
      const vapidKey = await notificationsApi.getVAPIDPublicKey()
      if (!vapidKey) return false

      // Запрашиваем разрешение и подписываемся
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      permissionState.value = Notification.permission as WebPushPermissionState

      if (!subscription) return false

      // Отправляем подписку на сервер
      const keys = subscription.toJSON().keys ?? {}
      await notificationsApi.registerWebPush({
        endpoint: subscription.endpoint,
        p256dhKey: keys.p256dh ?? '',
        authKey: keys.auth ?? '',
        userAgent: navigator.userAgent,
      })

      isSubscribed.value = true
      return true
    } catch (error) {
      console.error('Web Push subscription failed:', error)
      permissionState.value = Notification.permission as WebPushPermissionState
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function unsubscribe(): Promise<boolean> {
    if (!isSupported.value) return false
    isLoading.value = true

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await notificationsApi.unregisterWebPush(subscription.endpoint)
        await subscription.unsubscribe()
      }
      isSubscribed.value = false
      return true
    } catch (error) {
      console.error('Web Push unsubscribe failed:', error)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function checkSubscription() {
    if (!isSupported.value) return
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      isSubscribed.value = !!subscription
      permissionState.value = Notification.permission as WebPushPermissionState
    } catch {
      isSubscribed.value = false
    }
  }

  return {
    isSupported,
    permissionState,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    checkSubscription,
  }
}

/** Конвертация base64 VAPID ключа в Uint8Array */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length))
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
